import { create } from 'zustand'
import { currentGeometryOperation } from './operation'
import {
  findAllUnitCollisions,
  type PoseValidation,
  type UnitCollisionPair,
} from './validate'
import { dumpUnitGeometry, type UnitGeometry, type UnitGeometryDump } from './unitObb'

export type GeometryRejection = {
  timestamp: number
  operation: string
  unitId: string
  reason: 'GEOMETRIC_COLLISION'
  candidate: UnitGeometryDump
  collidingUnitIds: string[]
  collidingUnits: UnitGeometryDump[]
  snappedSides: unknown
}

type DebugState = {
  persistedCollisions: UnitCollisionPair[] | null
  lastRejection: GeometryRejection | null
  dismissPersisted: () => void
  clearRejection: () => void
}

export const geometryDebugStore = create<DebugState>((set) => ({
  persistedCollisions: null,
  lastRejection: null,
  dismissPersisted: () => set({ persistedCollisions: null }),
  clearRejection: () => set({ lastRejection: null }),
}))

const lastLogAt = new Map<string, number>()

export function isGeometryDebugEnabled(): boolean {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_GEOMETRY') === 'true') {
      return true
    }
  } catch {
    // localStorage no disponible
  }
  if (typeof window !== 'undefined' && (window as Window & { __DEBUG_GEOMETRY__?: boolean }).__DEBUG_GEOMETRY__ === true) {
    return true
  }
  const env = typeof import.meta !== 'undefined' ? (import.meta as ImportMeta & { env?: Record<string, string> }).env : undefined
  return env?.VITE_DEBUG_GEOMETRY === 'true'
}

function shouldEmitLog(key: string): boolean {
  if (import.meta.env?.MODE === 'test' || !isGeometryDebugEnabled()) return false
  const now = Date.now()
  const previous = lastLogAt.get(key) ?? 0
  if (now - previous < 500) return false
  lastLogAt.set(key, now)
  return true
}

export function recordGeometryRejections(rejections: GeometryRejection[]): void {
  if (rejections.length === 0) return
  if (import.meta.env?.DEV && isGeometryDebugEnabled()) {
    geometryDebugStore.setState({ lastRejection: rejections[0] })
  }
  for (const rejection of rejections) {
    const key = `${rejection.operation}:${rejection.unitId}:${rejection.collidingUnitIds.join(',')}`
    if (!shouldEmitLog(key)) continue
    console.error(
      `[3D INTEGRITY ERROR]\nUnit ${rejection.unitId} intersects ${rejection.collidingUnitIds.join(', ')}`,
      rejection,
    )
  }
}

export function rejectionFromValidation(
  unitId: string,
  validation: Extract<PoseValidation, { valid: false }>,
  snappedSides: unknown,
): GeometryRejection {
  return {
    timestamp: Date.now(),
    operation: currentGeometryOperation(),
    unitId,
    reason: 'GEOMETRIC_COLLISION',
    candidate: validation.candidate,
    collidingUnitIds: validation.collidingUnitIds,
    collidingUnits: validation.collidingUnits,
    snappedSides,
  }
}

/** Informe de una carga. No mueve unidades. Siempre visible, también sin DEBUG_GEOMETRY. */
export function publishPersistedProjectCollisions(items: Map<string, UnitGeometry>): UnitCollisionPair[] {
  const pairs = findAllUnitCollisions(items)
  if (pairs.length === 0) {
    geometryDebugStore.setState({ persistedCollisions: null })
    return pairs
  }
  geometryDebugStore.setState({ persistedCollisions: pairs })
  console.error(
    `[3D INTEGRITY ERROR]\nEl proyecto contiene unidades solapadas.\n${pairs
      .map((pair) => `Unit ${pair.unitA} intersects Unit ${pair.unitB}`)
      .join('\n')}`,
    pairs.map((pair) => ({
      unitA: pair.unitADump,
      unitB: pair.unitBDump,
    })),
  )
  return pairs
}

export function resetGeometryDebugForTests(): void {
  lastLogAt.clear()
  geometryDebugStore.setState({ persistedCollisions: null, lastRejection: null })
}

export function ghostDump(unit: UnitGeometry, id: string): UnitGeometryDump {
  return dumpUnitGeometry(unit, id)
}
